/**
 * DashboardPage — Event-centric home screen for the web app.
 *
 * Redesigned layout (matches desktop):
 * 1. SmartEventCreator bar (hero — "What's your next event?")
 * 2. Upcoming events timeline (EventRow list, click → EventWorkspace)
 * 3. Past events (collapsible)
 * 4. Quick actions (secondary)
 * 5. Scene stats (condensed)
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAuth } from '@workos-inc/authkit-react';
import { SmartEventCreator } from '../components/SmartEventCreator';
import { EventRow } from '../components/EventRow';
import {
  CalendarDays,
  Sparkles,
  Users,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  LogOut,
  Loader2,
  ChevronRight,
  ChevronDown,
  Crown,
  TrendingUp,
  Upload,
  Zap,
  BarChart2,
} from 'lucide-react';

export function DashboardPage() {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const stats = useQuery(api.dashboard.getStats);
  const allEvents = useQuery(api.events.list, {});
  const allEntities = useQuery(api.entities.list, {});
  const subscription = useQuery(api.billing.getSubscription);

  const tier = (subscription as any)?.tier ?? 'free';

  const [showPastEvents, setShowPastEvents] = useState(false);

  // Split events into upcoming and past
  const { upcomingEvents, pastEvents } = useMemo(() => {
    if (!allEvents) return { upcomingEvents: [], pastEvents: [] };
    const now = new Date();
    const upcoming = allEvents
      .filter((e) => {
        const dateStr = (e.metadata as any)?.date as string | undefined;
        if (!dateStr) return true;
        const status = (e.metadata as any)?.status as string | undefined;
        return new Date(dateStr) >= now && status !== 'cancelled';
      })
      .sort((a, b) => {
        const da = new Date(((a.metadata as any)?.date as string) ?? '').getTime() || 0;
        const db = new Date(((b.metadata as any)?.date as string) ?? '').getTime() || 0;
        return da - db;
      });

    const past = allEvents
      .filter((e) => {
        const dateStr = (e.metadata as any)?.date as string | undefined;
        if (!dateStr) return false;
        const status = (e.metadata as any)?.status as string | undefined;
        return new Date(dateStr) < now || status === 'cancelled';
      })
      .sort((a, b) => {
        const da = new Date(((a.metadata as any)?.date as string) ?? '').getTime() || 0;
        const db = new Date(((b.metadata as any)?.date as string) ?? '').getTime() || 0;
        return db - da;
      })
      .slice(0, 10);

    return { upcomingEvents: upcoming, pastEvents: past };
  }, [allEvents]);

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Header ─── */}
      <header className="border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/eventtools-logo.svg" alt="Event.Tools" className="h-7 w-auto" />
            <span className="font-semibold text-foreground">Event.Tools</span>
          </Link>

          <nav className="flex items-center gap-1">
            <NavButton to="/app/dashboard" active>
              <LayoutGrid className="w-4 h-4" />
              Dashboard
            </NavButton>
            <NavButton to="/app/editor">
              <Sparkles className="w-4 h-4" />
              Editor
            </NavButton>
            <NavButton to="/app/insights">
              <BarChart2 className="w-4 h-4" />
              Insights
            </NavButton>
          </nav>

          <div className="flex items-center gap-3">
            <TierBadge tier={tier} />
            <button
              onClick={() => void signOut()}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* ─── Main ─── */}
      <main className="container mx-auto px-6 py-8 max-w-3xl space-y-6">
        {/* Loading */}
        {stats === undefined && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {stats && (
          <>
            {/* Header */}
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {upcomingEvents.length > 0
                    ? `${upcomingEvents.length} upcoming event${upcomingEvents.length !== 1 ? 's' : ''}`
                    : 'No upcoming events \u2014 create one to get started'}
                </p>
              </div>
            </div>

            {/* ─── Hero: SmartEventCreator ─── */}
            <SmartEventCreator />

            {/* ─── Upcoming Events Timeline ─── */}
            {upcomingEvents.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold">Upcoming</h2>
                </div>
                <div className="space-y-2">
                  {upcomingEvents.map((event) => (
                    <EventRow
                      key={event._id}
                      event={event}
                      entities={allEntities}
                      onClick={() => navigate(`/app/event/${event._id}`)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* ─── Empty State ─── */}
            {allEvents && allEvents.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                  <CalendarDays className="w-10 h-10 text-muted-foreground/40" />
                </div>
                <h2 className="text-lg font-semibold">No events yet</h2>
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  Type your event name above and press Enter to create your first event instantly.
                </p>
              </div>
            )}

            {/* ─── Past Events (collapsible) ─── */}
            {pastEvents.length > 0 && (
              <section>
                <button
                  onClick={() => setShowPastEvents(!showPastEvents)}
                  className="flex items-center gap-2 text-base font-semibold text-muted-foreground hover:text-foreground transition-colors mb-3"
                >
                  {showPastEvents ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  Past ({pastEvents.length})
                </button>
                {showPastEvents && (
                  <div className="space-y-2">
                    {pastEvents.map((event) => (
                      <EventRow
                        key={event._id}
                        event={event}
                        entities={allEntities}
                        onClick={() => navigate(`/app/event/${event._id}`)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ─── Quick Actions (secondary) ─── */}
            <section>
              <h2 className="text-base font-semibold mb-3">Quick Actions</h2>
              <div className="grid grid-cols-3 gap-3">
                <QuickAction
                  icon={Upload}
                  label="Upload Flyer"
                  description="AI ingests event data"
                  color="bg-[#ef5228]"
                  onClick={() => navigate('/app/editor')}
                />
                <QuickAction
                  icon={Sparkles}
                  label="Open Editor"
                  description="Canvas workspace"
                  color="bg-purple-600"
                  onClick={() => navigate('/app/editor')}
                />
                <QuickAction
                  icon={TrendingUp}
                  label="Scene Insights"
                  description="Analytics & graphs"
                  color="bg-blue-600"
                  onClick={() => navigate('/app/insights')}
                />
              </div>
            </section>

            {/* ─── Scene Stats (condensed) ─── */}
            {(stats.totalEvents > 0 || stats.totalEntities > 0) && (
              <section>
                <h2 className="text-base font-semibold mb-3">Your Scene</h2>
                <div className="flex items-center gap-4 flex-wrap">
                  <StatChip icon={CalendarDays} label="Events" value={stats.totalEvents} />
                  <StatChip icon={Users} label="Entities" value={stats.totalEntities} />
                  <StatChip icon={ImageIcon} label="Assets" value={stats.totalAssets} />
                  <StatChip icon={Layers} label="Projects" value={stats.totalProjects} />
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function NavButton({
  to,
  active,
  children,
}: {
  to: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      }`}
    >
      {children}
    </Link>
  );
}

function QuickAction({
  icon: Icon,
  label,
  description,
  color,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-3.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-all text-left group"
    >
      <div
        className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{label}</div>
        <div className="text-xs text-muted-foreground truncate">{description}</div>
      </div>
    </button>
  );
}

function StatChip({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-left">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <span className="text-sm font-bold">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  if (tier === 'free') {
    return (
      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        <Zap className="w-3 h-3" />
        Free
      </span>
    );
  }
  if (tier === 'pro') {
    return (
      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#25a48f]/10 text-[#25a48f]">
        <Sparkles className="w-3 h-3" />
        Pro
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">
      <Crown className="w-3 h-3" />
      Creator
    </span>
  );
}
